import { ViewList, ViewSidebar } from '@tempo-io/sui-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

type FindingsView = 'findings' | 'detail';

interface FindingsViewPickerProps {
  active: FindingsView;
  onChange: (view: FindingsView) => void;
}

const ICON = 'size-4 shrink-0';

/**
 * List or one at a time, at the right of the filter row.
 *
 * An awkward control to place: it has two states, and it changes the shape of the page rather than
 * its content — so it has to be findable without reading as page-level navigation. Two plain targets
 * with no pill around them and no border on either half, the one you are on carrying a brand tint:
 * the same way a selected objective pill states itself two controls to the left, so the surface has
 * one way of saying "this is the one you are on" rather than two.
 *
 * Labels as well as icons. Without them the pair is smaller but says nothing — a reader who has not
 * met the icons has to press one to find out what it does, and this control is met once.
 *
 * It used to be the tab row's idiom one size down: small labels with the active one underlined, sat
 * on the hairline the tabs drew. Both of those are gone — the tabs, and then the rule, which now
 * runs above this row rather than under it, leaving the underline floating mid-air.
 */
export const FindingsViewPicker = ({ active, onChange }: FindingsViewPickerProps) => {
  const { t } = useTranslation();

  const options: { id: FindingsView; label: string; icon: ReactElement }[] = [
    {
      id: 'findings',
      label: t(($) => $.loopAgenticInsights.experimental.mode.list),
      icon: <ViewList className={ICON} aria-hidden="true" />,
    },
    {
      id: 'detail',
      label: t(($) => $.loopAgenticInsights.experimental.mode.detail),
      // Mirrored: the icon draws its rail on the right, and the Details reading puts its list of
      // findings on the left. An icon of a layout has to be that layout.
      icon: <ViewSidebar className={`${ICON} -scale-x-100`} aria-hidden="true" />,
    },
  ];

  return (
    <fieldset
      aria-label={t(($) => $.loopAgenticInsights.experimental.mode.label)}
      data-testid="agentic-x-mode-switch"
      className="flex items-center gap-1 border-0 p-0"
    >
      {options.map((option) => {
        const isSelected = option.id === active;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={isSelected}
            data-testid={`agentic-x-mode-${option.id}`}
            onClick={() => onChange(option.id)}
            className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors ${
              isSelected
                ? 'bg-bg-brand-subtler font-medium text-text-brand-primary'
                : 'text-text-neutral-secondary hover:bg-bg-neutral-subtle hover:text-text-neutral-primary'
            }`}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
};
